import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { ChevronLeft, ChevronRight, X, Plus, Phone, Trash2, Repeat } from "lucide-react";
import { ROLE_LABELS as ROLE_LABEL } from "@/lib/roles";
const WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function pad2(n) { return String(n).padStart(2, "0"); }
function ymd(y, m, d) { return `${y}-${pad2(m)}-${pad2(d)}`; }
function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
function firstWeekday(y, m) { return new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); }
function monthLabel(y, m) { return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("es-AR", { month: "long", year: "numeric", timeZone: "UTC" }); }
function todayAR() { return new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" }); }

function coversDay(g, day) {
  if ((g.dates || []).includes(day)) return true;
  if (g.date_from && g.date_to && g.date_from <= day && day <= g.date_to) return true;
  return false;
}

const navBtnStyle = { border: "1px solid var(--invict-bone-3)", background: "#fff", borderRadius: 6, padding: 6, cursor: "pointer", color: "#0A0A0A", display: "flex" };
const smallBtnStyle = { display: "flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: "#0A0A0A", fontSize: 11, fontWeight: 700, cursor: "pointer" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 200 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 18, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto" };

export function GuardsCalendar({ onChanged }) {
  const today = todayAR();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [month, setMonth] = useState(Number(today.slice(5, 7)));
  const [guards, setGuards] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dayModal, setDayModal] = useState(null);
  const [addForm, setAddForm] = useState(null);
  const [changingId, setChangingId] = useState(null);
  const [changeUserId, setChangeUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const monthStr = `${year}-${pad2(month)}`;

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get(`/passive-guards?month=${monthStr}`)];
      if (roster.length === 0) reqs.push(api.get("/users/roster"));
      const res = await Promise.all(reqs);
      setGuards(res[0].data);
      if (res[1]) setRoster(res[1].data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [monthStr]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const nDays = daysInMonth(year, month);
  const leadBlank = firstWeekday(year, month);
  const cells = [];
  for (let i = 0; i < leadBlank; i++) cells.push(null);
  for (let d = 1; d <= nDays; d++) cells.push(ymd(year, month, d));

  const entriesForDay = (day) => guards.filter((g) => coversDay(g, day));

  const openDay = (day) => { setDayModal(day); setAddForm(null); setChangingId(null); };
  const closeDay = () => { setDayModal(null); setAddForm(null); setChangingId(null); };
  const startAdd = () => setAddForm({ user_id: "", time_from: "", time_to: "", priority: 1, contact_phone: "", note: "" });

  const submitAdd = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      await api.post("/passive-guards", {
        user_id: addForm.user_id,
        dates: [dayModal],
        priority: Number(addForm.priority) || 1,
        time_from: addForm.time_from || null,
        time_to: addForm.time_to || null,
        contact_phone: addForm.contact_phone || null,
        note: addForm.note || null,
      });
      toast.success("Guardia asignada");
      setAddForm(null);
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const startChange = (g) => { setChangingId(g.id); setChangeUserId(g.user_id); };
  const submitChange = async (gid) => {
    if (!changeUserId) return;
    setBusy(true);
    try {
      await api.patch(`/passive-guards/${gid}`, { user_id: changeUserId });
      toast.success("Asignación cambiada");
      setChangingId(null);
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeEntry = async (g) => {
    const isRange = !!(g.date_from && g.date_to) && !(g.dates || []).includes(dayModal);
    if (isRange && !window.confirm(`Esto va a eliminar TODO el rango de ${g.user_name} (${g.date_from} → ${g.date_to}), no solo este día. ¿Confirmás?`)) return;
    setBusy(true);
    try {
      await api.delete(`/passive-guards/${g.id}`);
      toast.success("Asignación eliminada");
      await load();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const dayEntries = dayModal ? entriesForDay(dayModal) : [];

  return (
    <div className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="guards-calendar">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <button onClick={() => changeMonth(-1)} style={navBtnStyle} data-testid="calendar-prev-month"><ChevronLeft size={18} /></button>
        <div style={{ fontSize: 15, fontWeight: 900, textTransform: "capitalize", color: "#0A0A0A" }} data-testid="calendar-month-label">{monthLabel(year, month)}</div>
        <button onClick={() => changeMonth(1)} style={navBtnStyle} data-testid="calendar-next-month"><ChevronRight size={18} /></button>
      </div>
      <div style={{ fontSize: 10, color: "var(--invict-text-muted)", margin: "6px 0 10px" }}>
        Tocá un día para asignar, cambiar o quitar personal de retén. Cada mes se asigna manualmente.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {WEEKDAYS.map((w) => <div key={w} style={{ fontSize: 10, fontWeight: 800, color: "var(--invict-text-muted)", textAlign: "center", padding: "4px 0" }}>{w}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`b${i}`} />;
          const entries = entriesForDay(day);
          const isToday = day === today;
          return (
            <button key={day} onClick={() => openDay(day)} data-testid={`calendar-day-${day}`}
              style={{
                minHeight: 62, borderRadius: 6, border: isToday ? "2px solid var(--invict-red)" : "1px solid var(--invict-bone-3)",
                background: entries.length ? "#0A0A0A" : "#fff", padding: 4, cursor: "pointer", display: "flex", flexDirection: "column", gap: 2, textAlign: "left",
              }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: entries.length ? "#FFFBF4" : "#0A0A0A" }}>{Number(day.slice(8, 10))}</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {entries.slice(0, 3).map((g) => (
                  <span key={g.id} style={{ fontSize: 8, fontWeight: 800, background: "var(--invict-red)", color: "#fff", borderRadius: 8, padding: "1px 5px" }}>
                    {(g.user_name || "?").split(" ")[0].slice(0, 6)}
                  </span>
                ))}
                {entries.length > 3 && <span style={{ fontSize: 8, color: "rgba(255,251,244,0.7)" }}>+{entries.length - 3}</span>}
              </div>
            </button>
          );
        })}
      </div>
      {loading && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 10 }}>Cargando...</div>}

      {dayModal && (
        <div style={overlayStyle} onClick={closeDay} data-testid="day-modal-overlay">
          <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="day-modal">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0A0A0A" }} data-testid="day-modal-date">{dayModal}</div>
              <button onClick={closeDay} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0A0A0A" }} data-testid="day-modal-close"><X size={18} /></button>
            </div>

            {dayEntries.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 12 }} data-testid="day-modal-empty">Sin nadie asignado este día.</div>}

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {dayEntries.map((g) => (
                <div key={g.id} style={{ border: "1px solid var(--invict-bone-3)", borderRadius: 8, padding: 10 }} data-testid={`day-entry-${g.id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>{g.user_name}</span>
                    {g.user_deleted && <DeletedUserBadge id={g.id} />}
                    <span style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700 }}>{ROLE_LABEL[g.user_role] || g.user_role}</span>
                    {g.date_from && g.date_to && !(g.dates || []).includes(dayModal) && (
                      <span style={{ fontSize: 9, background: "var(--invict-bone-2)", color: "var(--invict-text-muted)", borderRadius: 6, padding: "1px 6px", fontWeight: 700 }}>RANGO {g.date_from}→{g.date_to}</span>
                    )}
                  </div>
                  {g.contact_phone && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}><Phone size={10} />{g.contact_phone}</div>}
                  {changingId === g.id ? (
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      <select value={changeUserId} onChange={(e) => setChangeUserId(e.target.value)} data-testid={`day-entry-change-select-${g.id}`} style={{ flex: 1, fontSize: 12, padding: 6, border: "1px solid var(--invict-bone-3)", borderRadius: 4 }}>
                        {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABEL[u.role] || u.role}</option>)}
                      </select>
                      <button onClick={() => submitChange(g.id)} disabled={busy} className="btn-primary" style={{ padding: "6px 10px", fontSize: 10 }} data-testid={`day-entry-change-confirm-${g.id}`}>Guardar</button>
                      <button onClick={() => setChangingId(null)} style={{ border: "none", background: "transparent", color: "var(--invict-text-muted)", cursor: "pointer" }}>Cancelar</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
                      <button onClick={() => startChange(g)} style={smallBtnStyle} data-testid={`day-entry-change-${g.id}`}><Repeat size={12} /> Cambiar</button>
                      <button onClick={() => removeEntry(g)} style={{ ...smallBtnStyle, color: "var(--invict-red)" }} data-testid={`day-entry-remove-${g.id}`}><Trash2 size={12} /> Quitar</button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {addForm ? (
              <form onSubmit={submitAdd} style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 12 }} data-testid="day-add-form">
                <div className="field"><label>Personal *</label>
                  <select required value={addForm.user_id} onChange={(e) => setAddForm({ ...addForm, user_id: e.target.value })} data-testid="day-add-user">
                    <option value="">Elegir...</option>
                    {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABEL[u.role] || u.role}</option>)}
                  </select>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div className="field"><label>Hora desde</label><input type="time" value={addForm.time_from} onChange={(e) => setAddForm({ ...addForm, time_from: e.target.value })} data-testid="day-add-time-from" /></div>
                  <div className="field"><label>Hora hasta</label><input type="time" value={addForm.time_to} onChange={(e) => setAddForm({ ...addForm, time_to: e.target.value })} data-testid="day-add-time-to" /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
                  <div className="field"><label>Prioridad</label><input type="number" min="1" value={addForm.priority} onChange={(e) => setAddForm({ ...addForm, priority: e.target.value })} data-testid="day-add-priority" /></div>
                  <div className="field"><label>Teléfono</label><input value={addForm.contact_phone} onChange={(e) => setAddForm({ ...addForm, contact_phone: e.target.value })} placeholder="+54 9 261..." data-testid="day-add-phone" /></div>
                </div>
                <div className="field"><label>Nota</label><input value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} data-testid="day-add-note" /></div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1, padding: 10 }} data-testid="day-add-submit">{busy ? "Guardando..." : "Asignar"}</button>
                  <button type="button" onClick={() => setAddForm(null)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "0 14px", cursor: "pointer" }} data-testid="day-add-cancel">Cancelar</button>
                </div>
              </form>
            ) : (
              <button onClick={startAdd} className="btn-primary" style={{ width: "100%", padding: 10 }} data-testid="day-add-open"><Plus size={14} style={{ display: "inline", marginRight: 6 }} />Agregar persona a este día</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
